import type { EventImpactTier } from '../../types';

type Maybe<T> = T | null | undefined;

export interface BackendOpportunityEvent {
  title: string;
  venue: Maybe<string>;
  category: Maybe<string>;
  attendance: Maybe<number>;
  relevance_score: Maybe<number>;
  compression_score?: Maybe<number>;
  compression_label?: Maybe<string>;
}

const COMPRESSION_LABEL_TO_TIER: Record<string, EventImpactTier> = {
  HIGH: 'high',
  MODERATE: 'moderate',
  LOW: 'low',
  MINIMAL: 'minimal',
  TRAFFIC_ONLY: 'traffic_only',
};

export const EVENT_IMPACT_TIER_RANK: Record<EventImpactTier, number> = {
  high: 5,
  moderate: 4,
  low: 3,
  minimal: 2,
  traffic_only: 1,
};

export function normalizeEventImpactTier(value: Maybe<string>): EventImpactTier | null {
  const label = String(value || '').trim().toUpperCase();
  return COMPRESSION_LABEL_TO_TIER[label] ?? null;
}

export function eventImpactTierFromCompressionScore(
  score: Maybe<number>,
): EventImpactTier | null {
  if (typeof score !== 'number' || !Number.isFinite(score) || score <= 0) return null;
  if (score >= 8) return 'high';
  if (score >= 5) return 'moderate';
  if (score >= 3) return 'low';
  return 'minimal';
}

export function resolveEventImpactTier(
  compressionLabel: Maybe<string>,
  compressionScore: Maybe<number>,
): EventImpactTier | null {
  return (
    normalizeEventImpactTier(compressionLabel) ??
    eventImpactTierFromCompressionScore(compressionScore)
  );
}

function normalizeKeyPart(value: string) {
  return value
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
}

export function buildNearDuplicateEventKey(dateIso: string, title: string, venue: Maybe<string>) {
  const d = String(dateIso || '').slice(0, 10);
  return `${d}|${normalizeKeyPart(title)}|${normalizeKeyPart(venue || '')}`;
}

export function dedupeNearIdenticalEvents(
  dateIso: string,
  events: BackendOpportunityEvent[],
): BackendOpportunityEvent[] {
  const bestByKey = new Map<string, BackendOpportunityEvent>();

  for (const ev of events) {
    const title = String(ev?.title || '').trim();
    if (!title) continue;
    const key = buildNearDuplicateEventKey(dateIso, title, ev?.venue);

    const current = bestByKey.get(key);
    if (!current) {
      bestByKey.set(key, ev);
      continue;
    }

    const currRel = typeof current.relevance_score === 'number' ? current.relevance_score : -1;
    const nextRel = typeof ev.relevance_score === 'number' ? ev.relevance_score : -1;
    if (nextRel > currRel) {
      bestByKey.set(key, ev);
      continue;
    }

    if (nextRel === currRel) {
      const currAtt = typeof current.attendance === 'number' ? current.attendance : -1;
      const nextAtt = typeof ev.attendance === 'number' ? ev.attendance : -1;
      if (nextAtt > currAtt) {
        bestByKey.set(key, ev);
      }
    }
  }

  return Array.from(bestByKey.values());
}

export function rankEventsByImpact(events: BackendOpportunityEvent[]) {
  const scoreRel = (ev: BackendOpportunityEvent) =>
    typeof ev.relevance_score === 'number' ? ev.relevance_score : -1;
  const scoreAtt = (ev: BackendOpportunityEvent) =>
    typeof ev.attendance === 'number' ? ev.attendance : -1;

  return [...events].sort((a, b) => {
    const rel = scoreRel(b) - scoreRel(a);
    if (rel !== 0) return rel;
    const att = scoreAtt(b) - scoreAtt(a);
    if (att !== 0) return att;
    return String(a.title).localeCompare(String(b.title));
  });
}

export function computeEventCompressionScore(
  dayCompressionScore: Maybe<number>,
  eventRelevanceScore: Maybe<number>,
) {
  // The backend returns a coarse 1-10 compression score per day. To reduce UI quantization,
  // we gently modulate by relevance (0-1), keeping the value within [base-0.5, base].
  const base =
    typeof dayCompressionScore === 'number' && Number.isFinite(dayCompressionScore)
      ? dayCompressionScore
      : 0;
  const rel =
    typeof eventRelevanceScore === 'number' && Number.isFinite(eventRelevanceScore)
      ? eventRelevanceScore
      : 1;

  const clampedRel = Math.max(0, Math.min(1, rel));
  const adjusted = base + (clampedRel - 1); // base-1..base; rel usually 0.5..1 so base-0.5..base
  return Math.max(0, Math.min(10, adjusted));
}

export function compressionPercentFromEventScores(
  eventScores: Array<Maybe<number>> | null | undefined,
) {
  // Compute a smooth aggregate (not just max) so multiple events raise compression slightly.
  // Scores can be either 0-10 or 0-100; normalize to 0-10 first.
  const safeEventScores = Array.isArray(eventScores) ? eventScores : [];
  const scores0to10 = safeEventScores
    .map((v) => (typeof v === 'number' && Number.isFinite(v) ? v : null))
    .filter((v): v is number => v !== null)
    .map((v) => (v > 0 && v <= 10 ? v : v / 10))
    .filter((v) => v > 0);

  if (scores0to10.length === 0) return null;

  const max = Math.max(...scores0to10);
  const beta = 2.0; // Higher = closer to max (less boost from additional events)
  const sumExp = scores0to10.reduce((acc, s) => acc + Math.exp(beta * (s - max)), 0);
  const combined = max + (1 / beta) * Math.log(sumExp);
  const combinedClamped = Math.max(0, Math.min(10, combined));

  return combinedClamped * 10; // percent
}
